import { useState } from 'react';
import Button from './ui/Button';
import ReadonlyField from './ui/ReadonlyField';
import InfoPanel from './ui/InfoPanel';
import locales from '../locales.json';
import { useToast } from '../app/ToastProvider';
import { $app } from '../app/App';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const { EXISTING_ACCOUNT_TEXTS, UNVERIFIED_ACCOUNT_TEXTS, COMMON_TEXTS, API_TEXTS } = locales;

export const ACCOUNT_VARIANT = {
  registered: 'registered',
  unverified: 'unverified',
};

const VARIANTS = {
  [ACCOUNT_VARIANT.registered]: {
    texts: EXISTING_ACCOUNT_TEXTS,
    recaptchaAction: 'bandi_card_event',
    send: (email, recaptchaToken) => $app.sendCardEvent(email, undefined, recaptchaToken),
  },
  [ACCOUNT_VARIANT.unverified]: {
    texts: UNVERIFIED_ACCOUNT_TEXTS,
    recaptchaAction: 'bandi_double_opt_in_event',
    send: (email, recaptchaToken) => $app.sendDoubleOptInEvent(email, undefined, recaptchaToken),
  },
};

const ExistingAccount = ({ email, onChangeEmail, variant = ACCOUNT_VARIANT.registered }) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { texts, recaptchaAction, send } = VARIANTS[variant];
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const showToast = useToast();

  const handleSend = async () => {
    setLoading(true);

    try {
      if (!executeRecaptcha) {
        showToast({ title: COMMON_TEXTS.error, description: API_TEXTS.recaptcha_failed }, 'error');
        return;
      }

      const recaptchaToken = await executeRecaptcha(recaptchaAction);
      await send(email, recaptchaToken);
      setSent(true);
      showToast({ title: texts.sentLead, description: texts.sentBody }, 'success');
    } catch (error) {
      showToast({ title: COMMON_TEXTS.error, description: error.message ?? API_TEXTS.clientError }, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="sheet email email--known">
      <h2 className="sheet__eyebrow">{texts.section}</h2>
      <h1 className="sheet__title">{texts.title}</h1>

      <div className="sheet__cols email__field">
        <ReadonlyField
          size="large"
          label={texts.emailLabel}
          value={email}
          action={
            <Button variant="link" onClick={onChangeEmail}>
              {texts.changeEmail}
            </Button>
          }
        />
      </div>

      <p className="sheet__body">{texts.note}</p>

      <div className="sheet__actions">
        <Button variant="ghost" block onClick={onChangeEmail}>
          {COMMON_TEXTS.back}
        </Button>
        <Button variant="primary" block disabled={sent} onClick={handleSend} loading={loading}>
          {texts.submit}
        </Button>
      </div>

      {texts.facts && (
        <InfoPanel>
          <h3 className="info__title">{texts.infoTitle}</h3>
          <ul className="facts">
            {texts.facts.map((fact) => (
              <li key={fact.title} className="facts__item">
                <b className="facts__title">{fact.title}</b>
                <span className="facts__text">{fact.text}</span>
              </li>
            ))}
          </ul>
        </InfoPanel>
      )}
    </div>
  );
};

export default ExistingAccount;
